import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import { api, formatApiError, setSessionExpiredHandler } from "../lib/api";
import SessionExpiredModal from "../components/SessionExpiredModal";
const AuthContext = createContext({});
export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sessionExpired, setSessionExpired] = useState(false);
  const validatingRef = useRef(false);

  const refresh = useCallback(async () => {
    if (validatingRef.current) return; // Prevent concurrent validations
    validatingRef.current = true;

    try {
      // Verify token is still valid by calling /auth/me with timeout
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000); // 5 second timeout

      try {
        const { data } = await api.get("/auth/me", { signal: controller.signal });
        setUser(data);
        setSessionExpired(false); // Clear modal if token is valid
      } finally {
        clearTimeout(timeoutId);
      }
    } catch (error) {
      // Token is invalid, expired, or request failed - clear auth state
      console.log("Token validation failed:", error.message);
      localStorage.removeItem("orello_token");
      setUser(null);
    } finally {
      setLoading(false);
      validatingRef.current = false;
    }
  }, []);

  // Validate token ONLY on app load (not on re-renders)
  useEffect(() => {
    refresh();
  }, []); // Empty dependency array - run only once on mount

  // Set up API session expiration handler
  useEffect(() => {
    setSessionExpiredHandler(handleSessionExpired);
  }, []);

  // Detect browser back/forward navigation
  // Block back button from reaching login page
  useEffect(() => {
    const handlePopState = () => {
      console.log('🔄 Browser back/forward detected');

      // If user tried to go back to login page, immediately go forward
      // This blocks the back button from leaving the app
      if (window.location.pathname === "/login") {
        console.log('⛔ Blocked back to login - going forward');
        window.history.forward();
        // Show session expired modal to explain why they can't go back
        setSessionExpired(true);
        return;
      }

      refresh();
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [refresh]);
  const login = async (phone, password) => {
    try {
      const { data } = await api.post("/auth/login", { phone, password });
      localStorage.setItem("orello_token", data.token);
      setUser(data.user);
      setSessionExpired(false); // Clear session expired state on successful login
      return { ok: true, user: data.user };
    } catch (e) { return { ok: false, error: formatApiError(e.response?.data?.detail) }; }
  };

  const loginWithOtp = async (user, token) => {
    localStorage.setItem("orello_token", token);
    setUser(user);
    setSessionExpired(false); // Clear session expired state on successful OTP login
  };
  const register = async (payload) => {
    try {
      const { data } = await api.post("/auth/register", payload);
      // New flow: user is NOT created until OTP verification
      // No token or user data in response yet
      return { ok: true, otp_required: true, phone: data.phone, email: data.email };
    } catch (e) { return { ok: false, error: formatApiError(e.response?.data?.detail) }; }
  };
  const verifyOtp = async (phone, otp) => {
    try {
      const { data } = await api.post("/auth/verify-otp", { phone, otp });
      // After OTP verification, user is created and token is returned
      localStorage.setItem("orello_token", data.token);
      setUser(data.user);
      return { ok: true, user: data.user, token: data.token };
    } catch (e) { return { ok: false, error: formatApiError(e.response?.data?.detail) }; }
  };
  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    localStorage.removeItem("orello_token");
    setUser(null);
    setSessionExpired(false);
    // Redirect to login
    window.location.href = "/login";
  };

  const handleSessionExpired = () => {
    // Show modal without silently redirecting
    localStorage.removeItem("orello_token");
    setUser(null);
    setSessionExpired(true);
  };

  const dismissSessionExpired = () => {
    setSessionExpired(false);
    // Only redirect if not already on login page
    if (window.location.pathname !== "/login") {
      window.location.href = "/login";
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        login,
        loginWithOtp,
        register,
        verifyOtp,
        logout,
        refresh,
        sessionExpired,
        handleSessionExpired,
        dismissSessionExpired
      }}
    >
      <SessionExpiredModal isOpen={sessionExpired} onDismiss={dismissSessionExpired} />
      {children}
    </AuthContext.Provider>
  );
}
export const useAuth = () => useContext(AuthContext);